'use client';

import * as React from 'react';

/**
 * How a pack glyph takes color.
 *
 * - `mono`: the file is used as a shape only (CSS mask) and painted with
 *   `currentColor`, so it follows text color like Lucide.
 * - `original`: the file keeps its own colors (duotone, brand marks) and is
 *   drawn as an image.
 *
 * Both load by URL as images; tenant markup never enters the DOM.
 */
export type IconColorMode = 'mono' | 'original';

/**
 * One pack glyph. Hosts map `custom/{slug}` → this shape from the
 * tenant-config `icons` row (`custom` means not Lucide, not the tenant name).
 * hen-ui does not fetch; the browser loads `url` as an image.
 */
export interface IconRegistryEntry {
  /** `https:` URL or a base64 `data:image/svg+xml` URL. */
  url: string;
  colorMode: IconColorMode;
}

/** Host-injected pack. Keys are glyph ids (`custom/{slug}`). */
export type IconRegistryMap = Readonly<
  Record<string, IconRegistryEntry | undefined>
>;

const IconRegistryContext = React.createContext<IconRegistryMap | null>(null);

/**
 * Optional host wrap that supplies extra glyphs to {@link Icon}.
 *
 * Missing or empty provider = today's Lucide-only behavior. Do not fetch
 * tenant-config from this package — the host already has publisher identity.
 *
 * @example
 * <IconRegistry value={{ 'custom/badge-check': { url, colorMode: 'mono' } }}>
 *   <Icon icon="check" />
 *   <Button iconAfter="custom/badge-check" />
 * </IconRegistry>
 */
export function IconRegistry({
  value,
  children,
}: {
  value: IconRegistryMap;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <IconRegistryContext.Provider value={value}>
      {children}
    </IconRegistryContext.Provider>
  );
}

export function useIconRegistry(): IconRegistryMap | null {
  return React.useContext(IconRegistryContext);
}
